import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useGetCompetitionsQuery } from 'src/api/competitions/getCompetitions';
import { useEffect, useState } from 'react';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import Iconify from 'src/components/iconify';
import { useNavigate } from 'react-router-dom';
import { RoutesKeys } from 'src/enums/routesKeys.enum';
import DeleteDialog from 'src/components/pages/DeleteDialog';
import { CompetitionType } from 'src/types/domain.types';
import { useDeleteCompetitionMutation } from 'src/api/competitions/deleteCompetition';
import { toast } from 'react-toastify';

export default function CompetitionsPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [deleteCompetition, setDeleteCompetition] =
    useState<CompetitionType | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const {
    data: competitions = [],
    isLoading,
    error,
    refetch,
  } = useGetCompetitionsQuery();
  const { mutateAsync: deleteCompetitionMutation } =
    useDeleteCompetitionMutation();

  useEffect(() => {
    if (error) {
      Promise.reject(error);
    }
  }, [error]);

  const handleOpenMenu = (event: any) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const handleEditCompetition = (competitionId: number) => {
    navigate(
      RoutesKeys.competitionsEdit.replace(':id', competitionId.toString())
    );
  };

  const handleDeleteCompetition = (competitionId: number) => {
    const competition = competitions.find(
      (competition) => competition.competition_id === competitionId
    );
    if (competition) {
      setOpenId(competitionId);
      setDeleteCompetition(competition);
    }
  };

  const deleteCompetitions = async (id: number) => {
    try {
      await deleteCompetitionMutation(id);
      refetch();
      toast.success('Competition deleted');
    } catch (error) {
      console.log(error);
      Promise.reject(error);
    }
  };

  const headers = [
    { id: 'competition_id', label: 'ID', alignRight: false },
    { id: 'name_en', label: 'Name', alignRight: false },
    { id: 'competition_feed_id', label: 'Feed ID', alignRight: false },
    { id: '', label: '', alignRight: true },
  ];

  return (
    <>
      <Helmet>
        <title>Competitions</title>
      </Helmet>
      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Competitions
          </Typography>
          <Box>
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-outline' />}
              onClick={() => navigate(RoutesKeys.competitionsCreate)}
            >
              Create Competition
            </Button>
          </Box>
        </Stack>

        <Card>
          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableCell
                          key={header.id}
                          align={header.alignRight ? 'right' : 'left'}
                        >
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competitions.length === 0 ? (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        style={{ padding: 20 }}
                      >
                        <Typography>No competitions found</Typography>
                      </Box>
                    ) : (
                      competitions.map((competition) => (
                        <TableRow
                          key={competition.competition_id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell component='td' scope='row'>
                            {competition.competition_id}
                          </TableCell>
                          <TableCell component='td' scope='row'>
                            {competition.name_en}
                          </TableCell>
                          <TableCell component='td' scope='row'>
                            {competition.competition_feed_id}
                          </TableCell>

                          <TableCell align='right'>
                            <IconButton
                              size='large'
                              color='inherit'
                              onClick={handleOpenMenu}
                              id={competition.competition_id.toString()}
                            >
                              <Iconify icon={'eva:more-vertical-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuList
          sx={{
            '& .MuiListSubheader-root:first-of-type': {
              py: 1,
              px: 2,
            },
          }}
        >
          <MenuItem onClick={() => handleEditCompetition(Number(openId))}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={() => handleDeleteCompetition(Number(openId))}>
            <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
            Delete
          </MenuItem>
        </MenuList>
      </Popover>

      {deleteCompetition && (
        <DeleteDialog
          openConfirm={true}
          type='Competition'
          desc='Are you sure you want to delete this competition?'
          setOpenConfirm={() => setDeleteCompetition(null)}
          deleteHandler={deleteCompetitions}
          id={openId ?? 0}
        />
      )}
    </>
  );
}
